import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Warehouse, Package, Landmark, CheckCircle2, Download, Printer } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { waitForImages } from '@/lib/waitForImages';
import { StockOverviewDocument, type StockDocBranding } from '@/components/partners/StockOverviewDocument';
import { exportToCsv } from '@/lib/exportCsv';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';
import { compareProducts } from '@/lib/productOrder';

interface Partner {
  id: string;
  serial_no: number;
  name: string;
  type: string;
  status: string;
}

interface StockRow {
  partner_id: string;
  sku: string;
  product_name: string | null;
  qty_on_hand: number;
  value_on_hand: number;
}

interface ArRow {
  partner_id: string;
  amount_bdt: number;
  amount_paid_bdt: number;
}

const LOW_STOCK_THRESHOLD = 5;

// Same print setup as ERP's Partner Accounts Summary: A4 landscape, no top
// page margin (the document's print-only <thead> spacer row gives pages 2+
// their top gap, and the negative margin cancels it on page 1), and the
// footer in the @page bottom margin boxes — the only place a browser can
// number pages.
const STOCK_PRINT_CSS = `@media print {
  .stock-overview-table { margin-top: -10mm; }
  @page {
    size: A4 landscape;
    margin: 0 0 11mm 0;
    @bottom-left {
      content: "Confidential  ·  System generated by Rx.K Seoul Admin";
      font: 8px Inter, system-ui, sans-serif;
      color: #6B7280;
      padding-left: 12mm;
      vertical-align: top;
    }
    @bottom-right {
      content: "Page " counter(page, decimal-leading-zero) " of " counter(pages, decimal-leading-zero);
      font: 600 8px Inter, system-ui, sans-serif;
      color: #1B2A4A;
      padding-right: 12mm;
      vertical-align: top;
    }
  }
}`;

const SIGNED_URL_TTL = 60 * 60;

async function pathToSignedUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { data } = await supabase.storage.from('invoice-assets').createSignedUrl(path, SIGNED_URL_TTL);
  return data?.signedUrl ?? null;
}
const TYPE_FILTERS = ['all', 'reseller', 'pharmacy', 'marketplace'] as const;
const TYPE_LABEL: Record<string, string> = { reseller: 'Reseller', pharmacy: 'Pharmacy', marketplace: 'Marketplace' };

// Same matrix + inline Report Sale modal as ERP's Stock Overview, both
// writing to the shared core_reseller_stock_v / core_record_reseller_sale
// RPC against the daily phone-call sales update. The partner name link
// goes to this app's own (read-only) reseller profile view — ERP's richer
// per-partner detail page (returns, AR ledger, serials) has no rxk
// equivalent yet.
interface SaleModalState {
  partnerId: string;
  partnerName: string;
  products: { sku: string; name: string; qty: number }[];
}

export default function AdminResellerStock() {
  const navigate = useNavigate();
  const { push } = useToast();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [stock, setStock] = useState<StockRow[]>([]);
  const [ar, setAr] = useState<ArRow[]>([]);
  // Due now (on units reported sold) and total due, both from core_partner_account_v
  // so they match the partner page exactly.
  const [dueNowByPartner, setDueNowByPartner] = useState<Map<string, number>>(new Map());
  const [totalDueByPartner, setTotalDueByPartner] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<(typeof TYPE_FILTERS)[number]>('all');
  const [saleModal, setSaleModal] = useState<SaleModalState | null>(null);
  const [saleForm, setSaleForm] = useState({ sku: '', qty: '', unit_price_bdt: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState<StockDocBranding | null>(null);
  const [printing, setPrinting] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(() => new Date());
  const docRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [partnerRes, stockRes, arRes, accountRes, tplRes] = await Promise.all([
      supabase.from('core_partners').select('id, serial_no, name, type, status').neq('type', 'courier').order('serial_no'),
      supabase.from('core_reseller_stock_v').select('partner_id, sku, product_name, qty_on_hand, value_on_hand'),
      supabase.from('core_ar_ledger').select('partner_id, amount_bdt, amount_paid_bdt').neq('status', 'paid'),
      supabase.from('core_partner_account_v').select('partner_id, due_now_bdt, total_due_bdt'),
      supabase.from('invoice_templates').select('issuer_name, concern_line, issuer_address, issuer_contact, issuer_email, logo_url').eq('doc_type', 'invoice').maybeSingle(),
    ]);
    const tpl = tplRes.data;
    setBranding({
      issuer_name: tpl?.issuer_name ?? null,
      concern_line: tpl?.concern_line ?? null,
      issuer_address: tpl?.issuer_address ?? null,
      issuer_contact: tpl?.issuer_contact ?? null,
      issuer_email: tpl?.issuer_email ?? null,
      logo_url: await pathToSignedUrl(tpl?.logo_url ?? null),
    });
    setGeneratedAt(new Date());
    setPartners((partnerRes.data as Partner[]) ?? []);
    setStock((stockRes.data as StockRow[]) ?? []);
    setAr((arRes.data as ArRow[]) ?? []);
    const accounts = (accountRes.data as { partner_id: string; due_now_bdt: number; total_due_bdt: number }[]) ?? [];
    setDueNowByPartner(new Map(accounts.map((a) => [a.partner_id, Number(a.due_now_bdt) || 0])));
    setTotalDueByPartner(new Map(accounts.map((a) => [a.partner_id, Number(a.total_due_bdt) || 0])));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Gift Box is excluded: it rides along on gift orders rather than being
  // sold by the reseller/pharmacy, so it has no "sold today" to report and
  // shouldn't count toward on-hand/value/Report Sale here.
  const columns = useMemo(() => {
    const bySku = new Map<string, string>();
    for (const s of stock) if (s.sku !== 'GIFT-BOX' && !bySku.has(s.sku)) bySku.set(s.sku, s.product_name ?? s.sku);
    return Array.from(bySku.entries())
      .map(([sku, name]) => ({ sku, name }))
      .sort(compareProducts);
  }, [stock]);

  const stockByPartner = useMemo(() => {
    const map = new Map<string, Map<string, { qty: number; value: number }>>();
    for (const s of stock) {
      if (!map.has(s.partner_id)) map.set(s.partner_id, new Map());
      map.get(s.partner_id)!.set(s.sku, { qty: s.qty_on_hand, value: s.value_on_hand });
    }
    return map;
  }, [stock]);

  const outstandingByPartner = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of ar) map.set(a.partner_id, (map.get(a.partner_id) ?? 0) + (a.amount_bdt - a.amount_paid_bdt));
    return map;
  }, [ar]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return partners
      .filter((p) => typeFilter === 'all' || p.type === typeFilter)
      .filter((p) => !term || p.name.toLowerCase().includes(term))
      .map((p) => {
        const cells = stockByPartner.get(p.id);
        const totalUnits = columns.reduce((sum, c) => sum + (cells?.get(c.sku)?.qty ?? 0), 0);
        const totalValue = columns.reduce((sum, c) => sum + (cells?.get(c.sku)?.value ?? 0), 0);
        return { partner: p, cells, totalUnits, totalValue, outstanding: Math.max(0, totalDueByPartner.get(p.id) ?? outstandingByPartner.get(p.id) ?? 0), dueNow: dueNowByPartner.get(p.id) ?? 0 };
      });
  }, [partners, typeFilter, search, stockByPartner, columns, outstandingByPartner, dueNowByPartner, totalDueByPartner]);

  const grandTotals = useMemo(
    () => ({
      perColumn: columns.map((c) => rows.reduce((sum, r) => sum + (r.cells?.get(c.sku)?.qty ?? 0), 0)),
      units: rows.reduce((sum, r) => sum + r.totalUnits, 0),
      value: rows.reduce((sum, r) => sum + r.totalValue, 0),
      outstanding: rows.reduce((sum, r) => sum + r.outstanding, 0),
      dueNow: rows.reduce((sum, r) => sum + Math.max(0, r.dueNow), 0),
    }),
    [rows, columns],
  );

  const openSaleModal = (r: (typeof rows)[number]) => {
    const products = columns
      .map((c) => ({ sku: c.sku, name: c.name, qty: r.cells?.get(c.sku)?.qty ?? 0 }))
      .filter((p) => p.qty > 0);
    if (products.length === 0) return;
    setSaleModal({ partnerId: r.partner.id, partnerName: r.partner.name, products });
    setSaleForm({ sku: products[0].sku, qty: '', unit_price_bdt: '', note: '' });
  };

  const selectedProduct = saleModal?.products.find((p) => p.sku === saleForm.sku) ?? null;

  const handleRecordSale = async (e: FormEvent) => {
    e.preventDefault();
    if (!saleModal) return;
    const qty = Number(saleForm.qty);
    if (!qty || qty <= 0) {
      push('error', 'Enter a quantity');
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc('core_record_reseller_sale', {
      p_partner_id: saleModal.partnerId,
      p_sku: saleForm.sku,
      p_qty: qty,
      p_unit_price_bdt: saleForm.unit_price_bdt ? Number(saleForm.unit_price_bdt) : null,
      p_note: saleForm.note || null,
      p_inventory_codes: null,
    });
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Sale recorded');
    setSaleModal(null);
    load();
  };

  const handlePrint = async () => {
    setPrinting(true);
    setGeneratedAt(new Date());
    if (docRef.current) await waitForImages(docRef.current);
    window.print();
    setPrinting(false);
  };

  const filterLabel = `${typeFilter === 'all' ? 'All partners' : `${TYPE_LABEL[typeFilter]} partners`}${search.trim() ? ` matching "${search.trim()}"` : ''}`;

  const downloadCsv = () => {
    const headers = ['Sr.', 'Partner', 'Type', 'Status', ...columns.map((c) => c.name), 'Total Units', 'Value', 'Due Now', 'Total Due'];
    const csvRows = rows.map((r) => [
      r.partner.serial_no,
      r.partner.name,
      r.partner.type,
      r.partner.status,
      ...columns.map((c) => r.cells?.get(c.sku)?.qty ?? 0),
      r.totalUnits,
      r.totalValue,
      Math.max(0, r.dueNow),
      r.outstanding,
    ]);
    csvRows.push(['', 'Total', '', '', ...grandTotals.perColumn, grandTotals.units, grandTotals.value, grandTotals.dueNow, grandTotals.outstanding]);
    exportToCsv(`stock-overview-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <style>{STOCK_PRINT_CSS}</style>
      <div className="space-y-5 print:hidden">
        <div>
          <h1 className="text-[22px] font-bold text-ink">Stock Overview</h1>
          <p className="text-[13px] text-ink-faint">Who's holding how much of what, right now — every partner, every product, one screen.</p>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Units On Hand (all partners)" value={grandTotals.units.toLocaleString()} icon={<Package className="w-4 h-4" />} accent="primary" />
              <StatCard label="Value On Hand" value={`৳${grandTotals.value.toLocaleString()}`} icon={<Warehouse className="w-4 h-4" />} accent="info" />
              <StatCard label="Due Now (on sold)" value={`৳${grandTotals.dueNow.toLocaleString()}`} icon={<CheckCircle2 className="w-4 h-4" />} accent="error" />
              <StatCard label="Total Due" value={`৳${grandTotals.outstanding.toLocaleString()}`} icon={<Landmark className="w-4 h-4" />} accent="warning" />
            </div>

            <Card>
              <CardHeader
                title="Stock by Partner"
                subtitle={`${rows.length} of ${partners.length} partners`}
                icon={<Warehouse className="w-4 h-4" />}
                action={
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={handlePrint} loading={printing} disabled={rows.length === 0 || !branding}>
                      <Printer className="w-3.5 h-3.5" /> Print
                    </Button>
                    <Button size="sm" variant="secondary" onClick={downloadCsv} disabled={rows.length === 0}>
                      <Download className="w-3.5 h-3.5" /> Download for Excel
                    </Button>
                  </div>
                }
              />
              <CardBody className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
                    <Input placeholder="Search by partner name..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
                  </div>
                  <div className="flex gap-1.5">
                    {TYPE_FILTERS.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTypeFilter(t)}
                        className={`px-3 py-1.5 rounded-lg text-[11.5px] font-semibold cursor-pointer ${
                          typeFilter === t ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
                        }`}
                      >
                        {t === 'all' ? 'All' : TYPE_LABEL[t]}
                      </button>
                    ))}
                  </div>
                </div>

                {rows.length === 0 ? (
                  <EmptyState title={partners.length === 0 ? 'No partners yet' : 'No partners match'} />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="text-ink-faint text-[10.5px] uppercase tracking-wide">
                          <th className="text-right py-1.5 px-2">Sr.</th>
                          <th className="text-left py-1.5 px-2">Partner</th>
                          {columns.map((c) => (
                            <th key={c.sku} className="text-right py-1.5 px-2">
                              {c.name}
                            </th>
                          ))}
                          <th className="text-right py-1.5 px-2">Total Units</th>
                          <th className="text-right py-1.5 px-2">Value</th>
                          <th className="text-right py-1.5 px-2">Due Now</th>
                          <th className="text-right py-1.5 px-2">Total Due</th>
                          <th className="py-1.5 px-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {/* Sr. is the partner's own number (core_partners.serial_no),
                            the same as the partner list — gaps are expected. */}
                        {rows.map((r, i) => (
                          <tr key={r.partner.id} className="border-t border-border-subtle hover:bg-surface-muted">
                            <td className="py-2 px-2 text-right text-ink-faint" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {r.partner.serial_no}
                            </td>
                            <td className="py-2 px-2">
                              <button
                                type="button"
                                onClick={() => navigate(`/admin/resellers/${r.partner.id}/view`)}
                                className="font-semibold text-ink hover:text-primary-700 cursor-pointer text-left"
                              >
                                {r.partner.name}
                              </button>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-[10.5px] text-ink-faint capitalize">{r.partner.type}</span>
                                <Badge tone={r.partner.status === 'active' ? 'success' : 'neutral'}>{r.partner.status}</Badge>
                              </div>
                            </td>
                            {columns.map((c) => {
                              const qty = r.cells?.get(c.sku)?.qty ?? 0;
                              return (
                                <td
                                  key={c.sku}
                                  className={`py-2 px-2 text-right ${qty === 0 ? 'text-ink-disabled' : qty <= LOW_STOCK_THRESHOLD ? 'text-warning font-semibold' : 'text-ink'}`}
                                  style={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                  {qty || '—'}
                                </td>
                              );
                            })}
                            <td className="py-2 px-2 text-right font-bold text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {r.totalUnits}
                            </td>
                            <td className="py-2 px-2 text-right text-ink-faint" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              ৳{r.totalValue.toLocaleString()}
                            </td>
                            <td
                              className={`py-2 px-2 text-right font-semibold ${r.dueNow > 0 ? 'text-error' : 'text-ink-faint'}`}
                              style={{ fontVariantNumeric: 'tabular-nums' }}
                              title={r.dueNow < 0 ? 'Paid in advance' : 'Owed on units reported sold'}
                            >
                              ৳{Math.max(0, r.dueNow).toLocaleString()}
                            </td>
                            <td
                              className={`py-2 px-2 text-right font-semibold ${r.outstanding > 0 ? 'text-warning' : 'text-ink-faint'}`}
                              style={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              ৳{r.outstanding.toLocaleString()}
                            </td>
                            <td className="py-2 px-2 text-right">
                              <Button size="sm" variant="secondary" disabled={r.totalUnits === 0} onClick={() => openSaleModal(r)}>
                                <CheckCircle2 className="w-3.5 h-3.5" /> Report Sale
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-ink font-bold">
                          <td className="py-2 px-2" />
                          <td className="py-2 px-2 text-ink">Total</td>
                          {grandTotals.perColumn.map((v, i) => (
                            <td key={columns[i].sku} className="py-2 px-2 text-right text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {v}
                            </td>
                          ))}
                          <td className="py-2 px-2 text-right text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {grandTotals.units}
                          </td>
                          <td className="py-2 px-2 text-right text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            ৳{grandTotals.value.toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            ৳{grandTotals.dueNow.toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            ৳{grandTotals.outstanding.toLocaleString()}
                          </td>
                          <td className="py-2 px-2" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </>
        )}
      </div>

      {/* Print-only: the same rows as the table above (current filter and
          search), laid out like ERP's Partner Accounts Summary print. */}
      {branding && (
        <div ref={docRef} className="hidden print:block">
          <StockOverviewDocument
            branding={branding}
            columns={columns}
            rows={rows.map((r) => ({
              id: r.partner.id,
              name: r.partner.name,
              type: r.partner.type,
              inHand: Object.fromEntries(columns.map((c) => [c.sku, r.cells?.get(c.sku)?.qty ?? 0])),
              units: r.totalUnits,
              value: r.totalValue,
              dueNow: r.dueNow,
              totalDue: r.outstanding,
            }))}
            totals={grandTotals}
            generatedAt={generatedAt}
            filterLabel={filterLabel}
            headingFont="'Playfair Display', Georgia, serif"
          />
        </div>
      )}

      <Modal open={!!saleModal} onClose={() => setSaleModal(null)} title={`Report Sale — ${saleModal?.partnerName ?? ''}`} icon={<CheckCircle2 className="w-4 h-4" />} busy={busy}>
        <form onSubmit={handleRecordSale} className="space-y-3">
          <Field label="Product">
            <Select value={saleForm.sku} onChange={(e) => setSaleForm((f) => ({ ...f, sku: e.target.value, qty: '' }))}>
              {saleModal?.products.map((p) => (
                <option key={p.sku} value={p.sku}>
                  {p.name} ({p.qty} on hand)
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Quantity Sold">
            <Input
              type="number"
              min={1}
              max={selectedProduct?.qty}
              value={saleForm.qty}
              onChange={(e) => setSaleForm((f) => ({ ...f, qty: e.target.value }))}
              placeholder="0"
            />
          </Field>
          <Field label="Unit Price (৳, optional)">
            <Input
              type="number"
              min={0}
              value={saleForm.unit_price_bdt}
              onChange={(e) => setSaleForm((f) => ({ ...f, unit_price_bdt: e.target.value }))}
              placeholder="For your own record only"
            />
          </Field>
          <Field label="Note (optional)">
            <Textarea rows={2} value={saleForm.note} onChange={(e) => setSaleForm((f) => ({ ...f, note: e.target.value }))} />
          </Field>
          <Button type="submit" loading={busy} className="w-full" disabled={!saleForm.qty || Number(saleForm.qty) <= 0}>
            Record Sale
          </Button>
        </form>
      </Modal>
    </AppShell>
  );
}
